/**
 * basic-pitch-engine.js — share the audio-to-notes polyphonic transcription
 * engine (Spotify Basic Pitch on tf.js) with other tools.
 *
 * Usage:
 *   var res = await window.BasicPitchEngine.transcribe(audioBuffer, {
 *       slug: 'eight-bit-converter',
 *       onProgress: function(p){ ... },   // 0..1
 *       signal: abortSignal,              // optional
 *   });
 *   // res = { notes: [{startTimeSeconds, durationSeconds, pitchMidi, amplitude}],
 *   //         backend: 'webgl'|'wasm' }
 *
 * Why a shared module: audio-to-notes.blade.php inlined this whole engine —
 * backend probing, the stall watchdog, the WebGL→WASM in-flight recovery and
 * the long-file chunking are all hard-won fixes from prod incidents. The
 * eight-bit-converter needs the SAME transcription (a chiptune is notes →
 * NES channels), and copy-pasting that pipeline into a second blade would
 * fork the recovery logic on the first bug. Same rationale, same shape as
 * stem-separator.js, which was lifted out of vocal-remover.blade.php.
 *
 * audio-to-notes is deliberately left untouched: it is the highest-traffic
 * consumer and a regression there is expensive. This module is a faithful
 * port of its pipeline; when audio-to-notes next needs a change to the
 * engine, migrate it onto this module rather than editing both.
 *
 * Assets resolve from <meta> tags the Blade layer renders through cdn()
 * (so RU/intl origins route correctly), with static /vendor/ fallbacks.
 *
 * Style: var/function ES5 to match audio-to-notes-renderer.js / -chords.js
 * and to stay parseable by the older Chromium builds we still see in prod.
 */
(function(){
'use strict';

// ── Asset routing ──────────────────────────────────────────────────────
// Blade renders the cdn()-resolved URLs into <meta> tags; we read them here
// instead of hard-coding an origin. Falls back to the static /vendor/ path
// so a page that forgets the meta tags still works from the local origin.
function metaUrl(name, fallback){
    try {
        var el = document.querySelector('meta[name="' + name + '"]');
        if (el && el.content) return el.content;
    } catch(_){}
    return fallback;
}
function assets(){
    return {
        tf:        metaUrl('bp-tf-url',       '/vendor/basic-pitch/tf.min.js'),
        lib:       metaUrl('bp-lib-url',      '/vendor/basic-pitch/basic-pitch-browser.js'),
        model:     metaUrl('bp-model-url',    '/vendor/basic-pitch/model/model.json'),
        wasmLib:   metaUrl('bp-tfwasm-url',   '/vendor/tfjs-wasm/tf-backend-wasm.min.js'),
        wasmBase:  metaUrl('bp-tfwasm-base',  '/vendor/tfjs-wasm/'),
        worker:    metaUrl('bp-worker-url',   '/js/a2n-bp-worker.js'),
        watchdog:  metaUrl('bp-watchdog-url', '/js/worker-watchdog.js'),
    };
}

// ── Engine state (shared across all callers on the page) ───────────────
var _depsLoaded = false;
var tfBackend   = null;   // 'webgl' | 'wasm' — committed once per page load

function report(slug, event, payload){
    try {
        if (typeof window.__ae === 'function') {
            window.__ae('tool_diagnostic', Object.assign(
                { slug: slug || 'basic-pitch-engine', event: event }, payload || {}
            ));
        }
    } catch(_){}
}

function loadScript(src, _retry){
    return new Promise(function(resolve, reject){
        var s = document.createElement('script');
        // Cache-bust the retry so a transient 5xx / aborted fetch isn't served
        // back from a poisoned HTTP cache (audio-to-notes prod incident: one
        // dropped request on mobile 3G killed the whole transcription).
        s.src = _retry ? (src + (src.indexOf('?') < 0 ? '?' : '&') + 'r=' + _retry) : src;
        s.onload  = function(){ resolve(); };
        s.onerror = function(){
            s.remove();
            if (!_retry) {
                setTimeout(function(){ loadScript(src, 1).then(resolve, reject); }, 900);
            } else {
                reject(new Error('bp_asset_load_failed'));
            }
        };
        document.head.appendChild(s);
    });
}

// Probe WebGL2 + max texture size. Returns 'webgl' if the GPU can run the
// BasicPitch model reliably, 'wasm' otherwise. Synchronous and cheap (~1 ms).
/**
 * ⚠️ The backend is not a speed ranking, it is a TRADE. Measured 2026-09-03
 * (30 s of audio, first run vs warm, RTX 4060):
 *
 *     webgl  14022 / 827 / 811 ms   ← ~13 s of run #1 is shader compilation,
 *                                     paid ON THE MAIN THREAD (page frozen)
 *     wasm    5950 / 4398 / 4377 ms ← in a worker, page keeps painting
 *
 * So WebGL is only worth its tax once the audio is long enough to amortise it
 * (break-even ≈ 55 s on that GPU, ≈ 240 s on a throttled machine). The older
 * probe below decided on MAX_TEXTURE_SIZE alone, which sent every short file
 * into a 13-second freeze — the shape of bug #1448 on /audio-to-notes.
 */
function chooseTfBackend(durationSec){
    // A device that already hit a WebGL shader-LINK failure in the tf.js
    // BasicPitch backend is remembered (bp_tf_webgl_broken), so the next visit
    // skips the doomed WebGL attempt and uses WASM directly. Adaptive +
    // browser-agnostic; shared key with the audio-to-notes blade copy.
    try { if (localStorage.getItem('bp_tf_webgl_broken') === '1') return 'wasm'; } catch(_){}
    try {
        var canvas = document.createElement('canvas');
        var gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
        if (!gl) return 'wasm';
        var maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE) | 0;
        canvas.width = canvas.height = 0;   // drop the ref so the GL ctx is GC'd
        if (maxTex < 8192) return 'wasm';
    } catch (e) {
        return 'wasm';
    }
    var dur = (typeof durationSec === 'number' && durationSec > 0) ? durationSec : 0;
    if (!dur) return 'wasm';                    // unknown length → never freeze
    var dm = navigator.deviceMemory || 0, hc = navigator.hardwareConcurrency || 0;
    // Weak device → the lane that never freezes, whatever the length: measured
    // on a 245 s file at ×4 throttle the two lanes' times overlap (wasm 38-102 s,
    // webgl 77-104 s) while WebGL's worst freeze is always the bigger one
    // (13.7-20.5 s vs 7.5-11.6 s). A lane that buys no time may not cost a page.
    if ((dm > 0 && dm <= 4) || (hc > 0 && hc <= 4)) return 'wasm';
    var tax = 0;
    try { tax = parseInt(localStorage.getItem('bp_gl_warmup_ms') || '0', 10) || 0; } catch(_){}
    if (!tax) tax = 13000;
    var kWasmMs = 300;                          // wasm ms per second of audio
    var breakEvenSec = tax / (kWasmMs * (1 - 1 / 3.5));
    return dur < breakEvenSec ? 'wasm' : 'webgl';
}

/* Main-thread tf.js + backend + BasicPitch.
 *
 * ⚠️ On the WASM path this is NOT needed when the worker lane is up: the worker
 * loads its own tf.js, its own .wasm and its own copy of the model. Doing both
 * meant compiling a 424 KB wasm module and a 2.8 MB graph model TWICE — measured
 * at 3867 ms of pure duplicate work on the first file, against ~2300 ms of
 * actual inference. So callers pass `preferWorker` and this only runs if the
 * worker could not take the job. */
async function loadDeps(slug){
    if (_depsLoaded && window.tf && window.BasicPitch) return;
    var A = assets();

    if (!window.tf) await loadScript(A.tf);

    if (tfBackend === null) tfBackend = chooseTfBackend(0);

    if (tfBackend === 'wasm' && window.tf) {
        try {
            await loadScript(A.wasmLib);
            // setWasmPaths must point at the DIRECTORY holding the .wasm
            // binaries — TF.js appends the file name itself.
            if (window.tf.wasm && window.tf.wasm.setWasmPaths) {
                window.tf.wasm.setWasmPaths(A.wasmBase);
            }
            await window.tf.setBackend('wasm');
            await window.tf.ready();
        } catch (e) {
            // WASM load itself failed — fall back to whatever tf.js auto-picked
            // at boot rather than failing before the model is even loaded.
            tfBackend = (window.tf && window.tf.getBackend && window.tf.getBackend()) || 'webgl';
            report(slug, 'wasm_load_failed', { msg: ((e && e.message) || '').substring(0, 200) });
        }
    }

    if (!window.BasicPitch) await loadScript(A.lib);
    _depsLoaded = true;
}

async function switchTfToWasm(slug){
    if (tfBackend === 'wasm') return;
    if (!window.tf) return;
    var A = assets();
    if (!window.tf.wasm) await loadScript(A.wasmLib);
    if (window.tf.wasm && window.tf.wasm.setWasmPaths) {
        window.tf.wasm.setWasmPaths(A.wasmBase);
    }
    await window.tf.setBackend('wasm');
    await window.tf.ready();
    tfBackend = 'wasm';
    report(slug, 'tf_switched_to_wasm', {});
}

// ── WASM inference worker ──────────────────────────────────────────────
/**
 * Measured 2026-08-13 on an RTX 4060 (headed Chromium, 30 s of audio):
 *
 *     backend   wall     painted frames   macrotask ticks
 *     webgl     1410 ms   51 fps           82 /s      page stays usable
 *     wasm      4883 ms    0.6 fps          0.4 /s    page FROZEN
 *
 * The two backends do not merely differ in speed — they differ in KIND.
 * WebGL's per-batch `await tensor.array()` is a real GPU readback, so the
 * event loop turns and the page keeps painting. WASM is synchronous CPU work
 * inside an async-looking loop: every `await` resolves immediately, which
 * drains microtasks only, so timers and rendering never get a turn.
 *
 * So the fix is not "make WASM yield" — it is to put each backend where it
 * belongs: WebGL on the main thread (fast, already non-blocking), WASM in a
 * worker (slow, but on someone else's thread).
 *
 * 14% of prod transcriptions take the WASM path (634 webgl / 103 wasm over
 * 45 days), and they are by definition the weakest devices — exactly the
 * users who reported "this is slow on old computers".
 *
 * ⚠️ WebGL CANNOT move into the worker: tf.js registers only cpu+webgl there
 * and `setBackend('webgl')` silently resolves to **'cpu'** (measured — no
 * throw, no warning), which would be far slower than either real backend.
 *
 * None of this is new code: /audio-to-notes has run exactly this lane since
 * bug #1164, with the shared WorkerWatchdog for hang detection. This module
 * was ported from that blade and silently LEFT THE LANE BEHIND — which is why
 * /audio-to-notes stays responsive on WASM and every other consumer of this
 * "faithful port" freezes. Reusing its worker (/js/a2n-bp-worker.js) and its
 * watchdog rather than writing a second copy is the point.
 */
var _bpWorker = null, _bpWorkerReady = false, _bpWorkerSeq = 0, _bpWorkerReadyPromise = null;
var _bpPending = new Map();   // id → { resolve, reject, onProgress }
var _bpWd = null, _bpWdLoadTried = false;

function _killBpWorker(){
    try { _bpWorker && _bpWorker.terminate(); } catch(_){}
    _bpWorker = null; _bpWorkerReady = false; _bpWorkerReadyPromise = null;
}

function _ensureWatchdogScript(){
    if (window.WorkerWatchdog || _bpWdLoadTried || typeof document === 'undefined') return;
    _bpWdLoadTried = true;
    try {
        var s = document.createElement('script');
        s.src = assets().watchdog; s.async = true;
        // Instantiate the moment it lands: a worker that dies before the next
        // message would otherwise never be tracked (deepfilter's self-load race).
        s.onload = function(){ try { _watchdog(); } catch(_){} };
        document.head.appendChild(s);
    } catch(_){}
}

function _watchdog(){
    if (_bpWd) return _bpWd;
    if (!window.WorkerWatchdog) { _ensureWatchdogScript(); return null; }
    _bpWd = window.WorkerWatchdog.create({
        name: 'bp_engine',
        getPending: function(){ return _bpPending; },
        terminateWorker: _killBpWorker,
        // 35 s with NO heartbeat (any progress/result message resets it). A
        // worker keeps beating while the tab is hidden, so — unlike the
        // main-thread bp_stall watchdog — no pause-on-hidden is needed.
        getHeartbeatTimeoutMs: function(){ return 35000; },
        getTickMs: function(){ return 5000; },
    });
    // Retroactive mark: a job enqueued before the watchdog script landed would
    // otherwise never be tracked.
    try { _bpPending.forEach(function(_p, id){ _bpWd.mark(id); }); } catch(_){}
    return _bpWd;
}

function initBpWorker(slug){
    // Escape hatch for the parity test (and for debugging a suspected worker
    // bug in prod without a deploy): forces the main-thread lane.
    if (window.__BP_DISABLE_WORKER) return Promise.resolve(false);
    if (_bpWorkerReadyPromise) return _bpWorkerReadyPromise;
    if (typeof Worker === 'undefined') return Promise.resolve(false);
    _ensureWatchdogScript();
    var A = assets();
    _bpWorkerReadyPromise = new Promise(function(resolve){
        try { _bpWorker = new Worker(A.worker); }
        catch (e) { _bpWorker = null; resolve(false); return; }

        var settled = false;
        var to = setTimeout(function(){ if (!settled) { settled = true; resolve(false); } }, 45000);
        _bpWorker.onmessage = function(ev){
            var m = ev.data || {};
            if (m.type === 'ready') {
                _bpWorkerReady = true;
                if (!settled) { settled = true; clearTimeout(to); resolve(true); }
                return;
            }
            if (m.type === 'init_error') {
                if (!settled) { settled = true; clearTimeout(to); resolve(false); }
                report(slug, 'bp_worker_init_error', { msg: (m.error || '').substring(0, 200) });
                return;
            }
            var wd = _watchdog(); if (wd) wd.touch(m.id);   // heartbeat on ANY per-job message
            var w = _bpPending.get(m.id);
            if (!w) return;
            if (m.type === 'progress')    { w.onProgress(m.pct); }
            else if (m.type === 'result') { _bpPending.delete(m.id); if (wd) wd.forget(m.id); w.resolve(m.notes); }
            else if (m.type === 'error')  { _bpPending.delete(m.id); if (wd) wd.forget(m.id); w.reject(new Error(m.error || 'bp_worker_error')); }
        };
        _bpWorker.onerror = function(){
            if (!settled) { settled = true; clearTimeout(to); resolve(false); }
            // Hard crash — reject every in-flight chunk so callers fall back to
            // the main thread instead of waiting on a promise that never settles.
            _bpPending.forEach(function(p){ try { p.reject(new Error('bp_worker_onerror')); } catch(_){} });
            _bpPending.clear();
            if (_bpWd) { try { _bpWd.dispose(); } catch(_){} _bpWd = null; }
        };
        _bpWorker.postMessage({ type: 'init', urls: {
            tf:          A.tf,
            wasmBackend: A.wasmLib,
            wasmDir:     A.wasmBase,
            basicPitch:  A.lib,
            model:       A.model,
        }});
    });
    return _bpWorkerReadyPromise;
}

function runBpChunkViaWorker(resampled, offsetSec, progressFn){
    return new Promise(function(resolve, reject){
        var id = ++_bpWorkerSeq;
        var wd = _watchdog();
        _bpPending.set(id, {
            resolve: resolve,
            reject: reject,
            onProgress: function(pct){ if (progressFn) try { progressFn(pct); } catch(_){} },
        });
        if (wd) wd.mark(id);
        // Copy into a fresh buffer so the transfer never neuters a shared view.
        var copy = new Float32Array(resampled);
        try {
            _bpWorker.postMessage({ type: 'infer', id: id, pcm: copy.buffer, offsetSec: offsetSec || 0 }, [copy.buffer]);
        } catch (e) {
            _bpPending.delete(id); if (wd) wd.forget(id); reject(e);
        }
    });
}

// ── Resample to the 22.05 kHz mono the model expects ───────────────────
function resample22050(audioBuffer){
    return new Promise(function(resolve, reject){
        var offCtx = new OfflineAudioContext(1, Math.ceil(audioBuffer.duration * 22050), 22050);
        var src = offCtx.createBufferSource();
        src.buffer = audioBuffer;
        src.connect(offCtx.destination);
        src.start();
        offCtx.startRendering().then(function(buf){ resolve(buf.getChannelData(0)); }).catch(reject);
    });
}

// Slice one window of a decoded buffer into its own AudioBuffer so each
// chunk's tensors can be released before the next run.
function sliceBuffer(audioBuffer, startSec, endSec){
    var sr    = audioBuffer.sampleRate;
    var from  = Math.max(0, Math.floor(startSec * sr));
    var to    = Math.min(audioBuffer.length, Math.floor(endSec * sr));
    var len   = Math.max(1, to - from);
    var chans = Math.min(2, audioBuffer.numberOfChannels);
    var out;
    if (window.__safeAudioBuffer) {
        out = window.__safeAudioBuffer({ numberOfChannels: chans, length: len, sampleRate: sr });
    } else {
        out = new AudioBuffer({ numberOfChannels: chans, length: len, sampleRate: sr });
    }
    for (var c = 0; c < chans; c++) {
        out.getChannelData(c).set(audioBuffer.getChannelData(c).subarray(from, to));
    }
    return out;
}

// ── One Basic Pitch pass over a resampled window ───────────────────────
// The stall watchdog is load-bearing: evaluateModel can hang with no
// resolution on constrained mobile (WebGL context lost when the tab is
// backgrounded, or a WASM job that never returns). Without it the user
// watches a frozen bar forever and gets neither notes nor an error.
// Every frames/pct callback is a heartbeat that resets the timer, and time
// spent hidden is discounted so backgrounding the tab isn't a false stall.
var STALL_MS = 45000;

async function runBpChunk(bp, resampled, offsetSec, progressFn){
    var cFrames = [], cOnsets = [], cContours = [];
    var settled = false, timer = null;
    var lastBeat = Date.now(), hiddenAt = null;
    function beat(){ lastBeat = Date.now(); }
    function onVis(){
        if (document.hidden) { if (hiddenAt === null) hiddenAt = Date.now(); }
        else if (hiddenAt !== null) { lastBeat += (Date.now() - hiddenAt); hiddenAt = null; }
    }
    try { document.addEventListener('visibilitychange', onVis); } catch(_){}
    var stall = new Promise(function(_, reject){
        timer = setInterval(function(){
            if (settled || document.hidden || hiddenAt !== null) return;
            if (Date.now() - lastBeat > STALL_MS) reject(new Error('bp_stall'));
        }, 2000);
    });
    try {
        await Promise.race([
            bp.evaluateModel(resampled, function(frames, onsets, contours){
                beat();
                for (var i = 0; i < frames.length; i++)   cFrames.push(frames[i]);
                for (var j = 0; j < onsets.length; j++)   cOnsets.push(onsets[j]);
                for (var k = 0; k < contours.length; k++) cContours.push(contours[k]);
            }, function(pct){ beat(); if (progressFn) progressFn(pct); }),
            stall
        ]);
    } finally {
        settled = true;
        if (timer) clearInterval(timer);
        try { document.removeEventListener('visibilitychange', onVis); } catch(_){}
    }
    // minNoteLen 9 (~105 ms) matches audio-to-notes: lower values flood the
    // result with sub-tone vibrato fragments instead of playable notes.
    var ev = BasicPitchUtils.outputToNotesPoly(cFrames, cOnsets, 0.5, 0.3, 9, true);
    ev = BasicPitchUtils.addPitchBendsToNoteEvents(cContours, ev);
    var notes = BasicPitchUtils.noteFramesToTime(ev);
    for (var n = 0; n < notes.length; n++) notes[n].startTimeSeconds += offsetSec;
    return notes;
}

// One-shot WebGL→WASM in-flight recovery for runtime shader-link failures
// and GPU-side stalls. Retrying on the SAME GL backend just repeats the
// failure; the WASM backend doesn't lose its context. A genuine OOM or
// codec error still propagates so the caller can show an honest message.
/* One chunk, on whichever lane suits the committed backend.
   WASM → the worker, so its synchronous CPU loop cannot freeze the page.
   ANY worker problem (init timeout, a missing op, a hard crash) silently
   degrades to the main thread: slower and janky, but it still produces notes,
   which beats refusing to transcribe. */
var _lastLane = 'main';   // which lane the most recent chunk actually ran on

async function runChunkOnBestLane(holder, resampled, offsetSec, progressFn, slug){
    if (tfBackend === 'wasm') {
        var up = false;
        try { up = await initBpWorker(slug); } catch(_){ up = false; }
        if (up && _bpWorkerReady) {
            try {
                var viaWorker = await runBpChunkViaWorker(resampled, offsetSec, progressFn);
                _lastLane = 'worker';
                return viaWorker;
            } catch (werr) {
                report(slug, 'bp_worker_fallback', { msg: ((werr && werr.message) || '').substring(0, 120) });
                _killBpWorker();
            }
        }
    }
    // Falling back (or never had a worker) — NOW the main thread needs tf.js,
    // a backend and the model. Loading them up front would have duplicated the
    // worker's own copies for nothing.
    _lastLane = 'main';
    await loadDeps(slug);
    if (!window.BasicPitch || !window.BasicPitchUtils) throw new Error('bp_module_missing');
    return await runBpChunk(holder.get(), resampled, offsetSec, progressFn);
}

async function runBpChunkRecovering(holder, resampled, offsetSec, progressFn, slug){
    try {
        return await runChunkOnBestLane(holder, resampled, offsetSec, progressFn, slug);
    } catch (err) {
        var msg = (err && err.message) || String(err);
        var isGl    = /shader|webgl|gl_|FRAMEBUFFER|texture/i.test(msg);
        var isStall = /bp_stall/.test(msg);
        if ((!isGl && !isStall) || tfBackend === 'wasm') throw err;
        // Remember a deterministic shader link/compile failure so this device
        // skips WebGL next visit (chooseTfBackend reads the same key); guarded so
        // a transient context-loss never sticks it to WASM.
        if (isGl && /link|compile|shader/i.test(msg)) { try { localStorage.setItem('bp_tf_webgl_broken', '1'); } catch(_){} }
        report(slug, isStall ? 'bp_stall_recover' : 'gl_inflight_recover', { msg: msg.substring(0, 200) });
        await switchTfToWasm(slug);
        // Drop the WebGL-bound model rather than rebuilding it here: the retry
        // below prefers the worker, which carries its own. holder.get() will
        // construct a fresh one on the new backend only if the retry actually
        // lands back on the main thread.
        holder.bp = null;
        try {
            // Retry on the WASM LANE, not just the WASM backend: a session that
            // starts on WebGL and falls over mid-file would otherwise finish on
            // the frozen main-thread path — the exact case `gl_inflight_recover`
            // fires 37× per 45 days, on top of the 103 that start there.
            return await runChunkOnBestLane(holder, resampled, offsetSec, progressFn, slug);
        } catch (_) {
            throw err;   // keep the root cause visible in telemetry
        }
    }
}

// ── Public API ─────────────────────────────────────────────────────────
var CHUNK_SEC = 60;   // same window audio-to-notes uses for long input

async function transcribe(audioBuffer, opts){
    opts = opts || {};
    var slug     = opts.slug || 'basic-pitch-engine';
    var onProg   = typeof opts.onProgress === 'function' ? opts.onProgress : null;
    var signal   = opts.signal || null;
    var chunkSec = opts.chunkSec || CHUNK_SEC;

    if (!audioBuffer || !audioBuffer.duration) throw new Error('bp_empty_input');

    /* Pick the backend BEFORE loading anything: the probe is a canvas call, it
       does not need tf.js. On the WASM path the worker is then given first
       refusal, and the main thread loads nothing at all unless the worker
       cannot take the job (runChunkOnBestLane does that lazily). This is what
       turns the worker from "+3.9 s of duplicated compile" into free. */
    if (tfBackend === null) tfBackend = chooseTfBackend(audioBuffer && audioBuffer.duration);
    var workerUp = false;
    if (tfBackend === 'wasm') {
        try { workerUp = await initBpWorker(slug); } catch(_){ workerUp = false; }
    }
    if (!workerUp) {
        await loadDeps(slug);
        if (!window.BasicPitch || !window.BasicPitchUtils) throw new Error('bp_module_missing');
    }
    if (signal && signal.aborted) throw new Error('Aborted by user');

    /* Lazy: constructing BasicPitch fetches and compiles the ~2.8 MB graph
       model. On the WASM lane the worker loads its OWN copy, so building one
       here too paid for the same download twice and delayed the first chunk
       (measured: 6689 ms vs 4995 ms upload→playable before this). The
       main-thread model is only needed if the worker lane is unavailable or
       fails, so build it at that moment instead. */
    var holder = {
        bp: null,
        get: function(){
            if (!this.bp) this.bp = new BasicPitch(assets().model);
            return this.bp;
        },
    };
    var duration = audioBuffer.duration;
    var windows  = [];
    for (var s = 0; s < duration; s += chunkSec) {
        windows.push([s, Math.min(duration, s + chunkSec)]);
    }

    var all = [];
    for (var w = 0; w < windows.length; w++) {
        if (signal && signal.aborted) throw new Error('Aborted by user');
        var win   = windows[w];
        var slice = windows.length === 1 ? audioBuffer : sliceBuffer(audioBuffer, win[0], win[1]);
        var res   = await resample22050(slice);
        var base  = w / windows.length;
        var span  = 1 / windows.length;
        var notes = await runBpChunkRecovering(holder, res, win[0], function(pct){
            if (onProg) try { onProg(base + span * (pct || 0)); } catch(_){}
        }, slug);
        for (var i = 0; i < notes.length; i++) all.push(notes[i]);
        res = null; slice = null;   // release before the next window
    }
    if (onProg) try { onProg(1); } catch(_){}

    // `lane` travels with the result so callers can put it in telemetry: it is
    // the only way prod tells us whether the WASM users are actually landing
    // off-thread, or quietly degrading back onto the frozen main-thread path.
    return { notes: all, backend: tfBackend, lane: _lastLane };
}

window.BasicPitchEngine = {
    transcribe: transcribe,
    /**
     * Start the WASM worker early, while the user is still picking/decoding a
     * file. The worker has to fetch and compile tf.js, a 424 KB .wasm and a
     * 2.8 MB graph model before it can infer anything — measured at ~1.9 s,
     * which is the whole remaining cost of the off-thread lane (+30% on the
     * first file, +6% after). Decoding a dropped file already takes about that
     * long, so warming in parallel hides it.
     *
     * No-op on the WebGL path (that lane never uses the worker) and safe to
     * call repeatedly — initBpWorker() caches its own promise.
     */
    warmup: function(slug){
        try {
            if (tfBackend === null) tfBackend = chooseTfBackend(0);
            if (tfBackend !== 'wasm') return false;
            initBpWorker(slug || 'basic-pitch-engine');
            return true;
        } catch(_){ return false; }
    },
    // Cheap pre-flight so callers can decide whether to offer the feature.
    isSupported: function(){
        try { return typeof OfflineAudioContext !== 'undefined'; } catch(_){ return false; }
    },
};

})();
